import React, { useEffect, useId, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import EmailDraftFields from '../../components/EmailDraftFields';
import { ErrorState } from '../../components/ui/States';
import Button from '../../components/ui/Button';
import type { DebitNoteEmail, DebitNoteEmailDraft } from '../../lib/debitNoteApi';

/**
 * "Email to vendor" on a draft vendor return (D44 #14, VR-6). The address starts
 * as the registered vendor's email on file and can be changed; the text starts as
 * the draft's and is sent as it stands (D180); the PDF goes with it. The return
 * becomes Sent only when the email went, so the dialog says what sending does
 * before anyone presses it.
 *
 * Rendered only while open; the page mounts it once it has the draft, and keeps it
 * mounted on a failed send, so the text survives; a mail that went closes it (D180).
 */
export const EmailToVendorDialog: React.FC<{
  debitNoteNumber: string;
  /** What the server drafted; `to` is '' when no email is on file. */
  draft: DebitNoteEmailDraft;
  goods: boolean;
  busy?: boolean;
  /** Why the last send failed, shown here because the page behind is covered. */
  error?: string | null;
  onCancel: () => void;
  onSend: (email: DebitNoteEmail) => void;
}> = ({ debitNoteNumber, draft, goods, busy = false, error = null, onCancel, onSend }) => {
  const [to, setTo] = useState(draft.to);
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [subjectError, setSubjectError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const toId = useId();
  const toRef = useRef<HTMLInputElement>(null);

  // Modal focuses its own panel a tick after opening; the address is what is checked first.
  useEffect(() => {
    const t = window.setTimeout(() => toRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  const missingTo = to.trim() === '';

  const handleSend = () => {
    const missingSubject = !subject.trim();
    const missingBody = !body.trim();
    setSubjectError(missingSubject ? 'Enter a subject.' : '');
    setBodyError(missingBody ? 'The email text is empty. Write it before sending.' : '');
    if (missingSubject || missingBody) return;
    onSend({ recipients: to.trim(), subject, body, draftVersion: draft.draftVersion });
  };

  return (
    <Modal
      isOpen
      title={`Email ${debitNoteNumber} to the vendor`}
      onClose={() => {
        if (!busy) onCancel();
      }}
      maxWidthClass="max-w-xl"
    >
      <div className="space-y-3">
        <p className="text-xs text-slate-600">
          The vendor&apos;s copy goes with this text: the invoice, the lines, quantities, unit prices, VAT, total and
          the reason. Once the email has gone the return is Sent
          {goods ? ' and its goods are held in their batch' : ''}; it can no longer be edited.
        </p>
        {!draft.to && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="email-no-address-on-file"
          >
            {draft.vendorMatched
              ? 'The vendor has no email on file. Type the address to send it to.'
              : 'This invoice is not matched to a registered vendor, so there is no email on file. Type the address to send it to.'}
          </div>
        )}
        <div>
          <label htmlFor={toId} className="block text-xs font-medium text-slate-600 mb-1">
            To
          </label>
          <input
            id={toId}
            ref={toRef}
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="accounts@vendor.com"
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs focus:border-primary-500"
          />
          <p className="mt-1 text-xs text-slate-500">Several addresses can be separated by commas.</p>
        </div>
        <EmailDraftFields
          subject={subject}
          onSubjectChange={setSubject}
          body={body}
          onBodyChange={setBody}
          attachment={draft.attachmentName}
          subjectError={subjectError}
          bodyError={bodyError}
          disabled={busy}
        />
        {error && <ErrorState message={error} />}
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onCancel} disabled={busy}>
            Back
          </Button>
          <Button variant="primary" onClick={handleSend} loading={busy} disabled={missingTo}>
            Send email
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EmailToVendorDialog;
